import React, { useRef, useState, useEffect } from 'react';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import AccessTimeIcon from '@material-ui/icons/AccessTime';

/**
 * Parse any user input format into { h, m }:
 * Supports:
 * - "4:30", "04:30"
 * - "430" (4h 30m), "0430" (4h 30m), "230" (2h 30m), "800" (8h 00m)
 * - "4" (4h 00m), "04" (4h 00m)
 * - "4.5" (4h 30m), "2.5" (2h 30m), "1.25" (1h 15m)
 * - "4.30" (4h 30m)
 */
export const parseTimelineHoursMinutes = (val) => {
  if (!val || typeof val !== 'string') return null;
  let str = val.replace(/hr/gi, '').trim();
  if (!str) return null;

  // Handle decimal hours like "1.5" or "4.5" or "4.30"
  if (/^\d+(\.\d+)?$/.test(str) && str.includes('.')) {
    const parts = str.split('.');
    const h = parseInt(parts[0] || '0', 10);
    const decStr = parts[1] || '0';
    // If user typed 4.30, treat .30 as 30 minutes
    let m = 0;
    if (decStr.length === 2 && parseInt(decStr, 10) < 60) {
      m = parseInt(decStr, 10);
    } else {
      const num = parseFloat(str);
      m = Math.round((num - h) * 60);
    }
    if (isNaN(h) || isNaN(m)) return null;
    return { h, m };
  }

  // Replace dot or space with colon
  str = str.replace(/[.\s]/g, ':').replace(/:{2,}/g, ':');
  const clean = str.replace(/[^0-9:]/g, '');
  if (!clean) return null;

  if (clean.includes(':')) {
    const [hStr, mStr] = clean.split(':');
    const h = parseInt(hStr || '0', 10);
    const m = mStr !== undefined && mStr !== '' ? parseInt(mStr, 10) : 0;
    if (isNaN(h) || isNaN(m)) return null;
    return { h, m };
  }

  // Pure digits without colon:
  // 3 digits: e.g. "430" -> 4 hours, 30 mins; "230" -> 2h 30m; "800" -> 8h 00m
  if (clean.length === 3) {
    const h = parseInt(clean.slice(0, 1), 10);
    const m = parseInt(clean.slice(1), 10);
    if (isNaN(h) || isNaN(m)) return null;
    return { h, m };
  }

  // 4 digits: e.g. "0430" -> 4 hours, 30 mins; "0800" -> 8 hours
  if (clean.length === 4) {
    const h = parseInt(clean.slice(0, 2), 10);
    const m = parseInt(clean.slice(2), 10);
    if (isNaN(h) || isNaN(m)) return null;
    return { h, m };
  }

  // 1 or 2 digits: e.g. "4" -> 4 hours; "08" -> 8 hours
  if (clean.length <= 2) {
    const h = parseInt(clean, 10);
    if (isNaN(h)) return null;
    return { h, m: 0 };
  }

  return null;
};

/**
 * Validates timeline duration string.
 * Returns { isValid, error }
 */
export const validateTimeline = (val, maxHours = 8, maxMinutes = null) => {
  if (!val || typeof val !== 'string' || !val.trim()) {
    return { isValid: true, error: null };
  }
  const clean = val.replace(/hr/gi, '').trim().replace(/[^0-9:.]/g, '');
  if (!clean) {
    return { isValid: true, error: null };
  }

  const parsed = parseTimelineHoursMinutes(clean);
  if (!parsed) {
    return { isValid: false, error: 'Add proper time line in hours' };
  }

  const { h, m } = parsed;

  // Minutes validation: 00 to 59 only
  if (m < 0 || m >= 60) {
    return { isValid: false, error: 'Add proper time line in hours' };
  }

  if (maxMinutes !== null && maxMinutes !== undefined) {
    if (h * 60 + m > maxMinutes) {
      return { isValid: false, error: 'Exceeds available timeline' };
    }
  } else if (maxHours !== null && maxHours !== undefined) {
    if (h < 0 || h > maxHours || (h === maxHours && m > 0)) {
      return { isValid: false, error: `Max ${maxHours} hours` };
    }
  } else {
    if (h < 0) {
      return { isValid: false, error: 'Add proper time line in hours' };
    }
  }

  return { isValid: true, error: null };
};

export const isOverEightHours = (val) => {
  if (!val || typeof val !== 'string') return false;
  const parsed = parseTimelineHoursMinutes(val);
  if (!parsed) return false;
  return (parsed.h * 60 + parsed.m) > 480;
};

/**
 * Formats timeline duration to HH:mm string.
 */
export const clampAndFormatTimeline = (val, maxHours = 8, maxMinutes = null) => {
  if (!val || typeof val !== 'string') return '';
  const parsed = parseTimelineHoursMinutes(val);
  if (!parsed) return '';

  let { h, m } = parsed;
  if (isNaN(h) || h < 0) h = 0;
  if (isNaN(m) || m < 0) m = 0;
  if (m > 59) m = 59;

  if (maxMinutes !== null && maxMinutes !== undefined) {
    if (h * 60 + m > maxMinutes) {
      h = Math.floor(maxMinutes / 60);
      m = maxMinutes % 60;
    }
  } else if (maxHours !== null && maxHours !== undefined) {
    if (h > maxHours || (h === maxHours && m > 0)) {
      h = maxHours;
      m = 0;
    }
  }

  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/**
 * Formats a total minute count to HH:mm hr format
 */
export const formatMinutesToTimeline = (totalMinutes) => {
  if (totalMinutes === null || totalMinutes === undefined || totalMinutes < 0) return '00:00 hr';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} hr`;
};

export const sanitizeTimelineInput = (raw, prevVal = '') => {
  if (!raw) return '';

  // Strip hr and anything not a digit or colon
  let val = String(raw).replace(/hr/gi, '').replace(/[^0-9:]/g, '');

  const isDeleting = val.length < prevVal.length;
  if (isDeleting) {
    return val;
  }

  // If user typed colon alone -> "00:"
  if (val === ':') {
    return '00:';
  }

  // If user typed 4 digits without colon (e.g. "0530" or "0800")
  if (/^\d{4}$/.test(val)) {
    let h = parseInt(val.slice(0, 2), 10);
    let m = parseInt(val.slice(2, 4), 10);
    if (h > 8 || h === 8) {
      return '08:00';
    }
    if (m > 59) m = 59;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  // If user typed 3 digits without colon (e.g. "530" or "800")
  if (/^\d{3}$/.test(val)) {
    let h = parseInt(val.slice(0, 1), 10);
    let m = parseInt(val.slice(1, 3), 10);
    if (h > 8 || h === 8) {
      return '08:00';
    }
    if (m > 59) m = 59;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  // If there's a colon:
  if (val.includes(':')) {
    let [hStr, ...mParts] = val.split(':');
    let mStr = mParts.join('');

    let h = parseInt(hStr || '0', 10);
    if (h > 8) {
      return '08:00';
    }

    let formattedH = hStr.length === 1 ? `0${hStr}` : hStr.slice(0, 2);

    // If hours is 8, minutes CANNOT be > 0
    if (h === 8) {
      if (mStr && mStr !== '0' && mStr !== '00') {
        return '08:00';
      }
      return mStr.length > 0 ? `08:${mStr.slice(0, 2)}` : '08:';
    }

    // Minutes: cannot exceed 2 digits, and first digit cannot be 6-9
    if (mStr.length >= 1) {
      let firstMinDigit = parseInt(mStr[0], 10);
      if (firstMinDigit > 5) {
        mStr = '59';
      } else if (mStr.length > 2) {
        mStr = mStr.slice(0, 2);
      }
    }

    return `${formattedH}:${mStr}`;
  }

  // Pure digits without colon:
  if (val.length === 1) {
    let d = parseInt(val, 10);
    if (d > 8) {
      return '08:00';
    }
    return val;
  }

  if (val.length === 2) {
    if (val.startsWith('0')) {
      let d = parseInt(val[1], 10);
      if (d > 8) {
        return '08:00';
      }
      return `${val}:`;
    }

    let h = parseInt(val[0], 10);
    let m1 = parseInt(val[1], 10);

    if (h > 8 || h === 8) {
      return '08:00';
    }

    if (m1 > 5) {
      m1 = 5;
    }

    return `0${h}:${m1}`;
  }

  return val.slice(0, 5);
};

const TimelineInputField = ({
  value = '',
  onChange,
  onBlur,
  onFocus,
  label = 'Add time line',
  id = 'task_timeline',
  name = 'timeline',
  error = false,
  helperText = '',
  disabled = false,
  required = false,
  fullWidth = true,
  className = '',
  size = 'medium',
  maxHours = 8,
  maxMinutes = null,
  helperTextColor = null,
  onMaxExceeded,
  ...otherProps
}) => {
  const parsed = parseTimelineHoursMinutes(value);
  const initialH = parsed ? String(parsed.h).padStart(2, '0') : '';
  const initialM = parsed ? String(parsed.m).padStart(2, '0') : '';

  const [hours, setHours] = useState(initialH);
  const [minutes, setMinutes] = useState(initialM);
  const [isFocused, setIsFocused] = useState(false);

  const hoursRef = useRef(null);
  const minutesRef = useRef(null);
  const lastExceededWarnRef = useRef(0);

  const triggerMaxExceeded = () => {
    const now = Date.now();
    if (onMaxExceeded && now - lastExceededWarnRef.current > 1500) {
      lastExceededWarnRef.current = now;
      onMaxExceeded();
    }
  };

  const effectiveMaxHours = maxMinutes !== null && maxMinutes !== undefined
    ? Math.floor(maxMinutes / 60)
    : (maxHours !== null && maxHours !== undefined ? maxHours : 99);

  useEffect(() => {
    if (!isFocused) {
      const p = parseTimelineHoursMinutes(value);
      setHours(p ? String(p.h).padStart(2, '0') : '');
      setMinutes(p ? String(p.m).padStart(2, '0') : '');
    }
  }, [value, isFocused]);

  const commitValue = (hVal, mVal) => {
    let cleanH = (hVal !== undefined ? hVal : (hoursRef.current ? hoursRef.current.value : hours) || '').trim();
    let cleanM = (mVal !== undefined ? mVal : (minutesRef.current ? minutesRef.current.value : minutes) || '').trim();

    if (!cleanH && !cleanM) {
      if (onChange) onChange('');
      return;
    }

    let h = parseInt(cleanH || '0', 10);
    let m = parseInt(cleanM || '0', 10);
    if (isNaN(h) || h < 0) h = 0;
    if (isNaN(m) || m < 0) m = 0;
    if (m > 59) m = 59;

    let exceeded = false;
    if (maxMinutes !== null && maxMinutes !== undefined) {
      if (h * 60 + m > maxMinutes) {
        h = Math.floor(maxMinutes / 60);
        m = maxMinutes % 60;
        exceeded = true;
      }
    } else if (maxHours !== null && maxHours !== undefined) {
      if (h > maxHours || (h === maxHours && m > 0)) {
        h = maxHours;
        m = 0;
        exceeded = true;
      }
    }

    if (exceeded) {
      triggerMaxExceeded();
    }

    const formattedH = String(h).padStart(2, '0');
    const formattedM = String(m).padStart(2, '0');
    setHours(formattedH);
    setMinutes(formattedM);
    if (hoursRef.current) hoursRef.current.value = formattedH;
    if (minutesRef.current) minutesRef.current.value = formattedM;

    const result = `${formattedH}:${formattedM}`;
    if (onChange) {
      onChange(result);
    }
  };

  const handleHoursChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    const currentM = (minutesRef.current ? minutesRef.current.value : minutes) || '00';

    if (!raw) {
      setHours('');
      if (onChange) onChange(`00:${currentM.padStart(2, '0')}`);
      return;
    }

    // Fast typing 3 or 4 digits (e.g. "0430" or "430" or "1530")
    if (raw.length >= 3) {
      const p = parseTimelineHoursMinutes(raw);
      if (p) {
        let h = p.h;
        let m = p.m > 59 ? 59 : p.m;
        let exceeded = false;

        if (maxMinutes !== null && maxMinutes !== undefined) {
          if (h * 60 + m > maxMinutes) {
            h = Math.floor(maxMinutes / 60);
            m = maxMinutes % 60;
            exceeded = true;
          }
        } else if (maxHours !== null && maxHours !== undefined) {
          if (h > maxHours || (h === maxHours && m > 0)) {
            h = maxHours;
            m = 0;
            exceeded = true;
          }
        }

        if (exceeded) {
          triggerMaxExceeded();
        }

        const formattedH = String(h).padStart(2, '0');
        const formattedM = String(m).padStart(2, '0');
        setHours(formattedH);
        setMinutes(formattedM);
        if (hoursRef.current) hoursRef.current.value = formattedH;
        if (minutesRef.current) minutesRef.current.value = formattedM;
        if (onChange) onChange(`${formattedH}:${formattedM}`);
        setTimeout(() => {
          minutesRef.current?.focus();
          minutesRef.current?.select();
        }, 0);
        return;
      }
    }

    // 1 digit typed
    if (raw.length === 1) {
      if (raw === '0') {
        setHours('0');
        if (onChange) onChange(`00:${currentM.padStart(2, '0')}`);
        return;
      }

      let num = parseInt(raw, 10);
      let exceeded = false;
      if (num > effectiveMaxHours) {
        num = effectiveMaxHours;
        exceeded = true;
      }

      // If num >= 3 OR num * 10 > effectiveMaxHours OR effectiveMaxHours <= 8:
      // This is a single digit hour (e.g. typing 3..9 or any digit that cannot have a 2nd digit).
      // Automatically pad to 0X and advance to minutes!
      const shouldAutoAdvance = num >= 3 || (num * 10 > effectiveMaxHours) || (effectiveMaxHours <= 8);

      if (shouldAutoAdvance) {
        const formatted = String(num).padStart(2, '0');
        setHours(formatted);
        if (hoursRef.current) hoursRef.current.value = formatted;

        let newM = currentM;
        if (maxMinutes !== null && maxMinutes !== undefined) {
          const curTotal = num * 60 + parseInt(currentM, 10);
          if (curTotal > maxMinutes) {
            newM = String(maxMinutes % 60).padStart(2, '0');
            setMinutes(newM);
            if (minutesRef.current) minutesRef.current.value = newM;
            exceeded = true;
          }
        } else if (maxHours !== null && maxHours !== undefined && num === maxHours) {
          newM = '00';
          setMinutes('00');
          if (minutesRef.current) minutesRef.current.value = '00';
        }

        if (exceeded) {
          triggerMaxExceeded();
        }

        if (onChange) onChange(`${formatted}:${newM.padStart(2, '0')}`);
        setTimeout(() => {
          minutesRef.current?.focus();
          minutesRef.current?.select();
        }, 0);
        return;
      }

      // If num is 1 or 2 and could potentially be a 2-digit number (e.g. 10..15 or 20..24)
      if (exceeded) {
        triggerMaxExceeded();
      }
      setHours(raw);
      if (hoursRef.current) hoursRef.current.value = raw;
      if (onChange) onChange(`${raw.padStart(2, '0')}:${currentM.padStart(2, '0')}`);
      return;
    }

    // 2 digits typed (e.g. "15")
    let num = parseInt(raw.slice(0, 2), 10);
    let exceeded = false;
    if (num > effectiveMaxHours) {
      num = effectiveMaxHours;
      exceeded = true;
    }
    const formatted = String(num).padStart(2, '0');
    setHours(formatted);
    if (hoursRef.current) hoursRef.current.value = formatted;

    let newM = currentM;
    if (maxMinutes !== null && maxMinutes !== undefined) {
      const curTotal = num * 60 + parseInt(currentM, 10);
      if (curTotal > maxMinutes) {
        newM = String(maxMinutes % 60).padStart(2, '0');
        setMinutes(newM);
        if (minutesRef.current) minutesRef.current.value = newM;
        exceeded = true;
      }
    } else if (maxHours !== null && maxHours !== undefined && num === maxHours) {
      newM = '00';
      setMinutes('00');
      if (minutesRef.current) minutesRef.current.value = '00';
    }

    if (exceeded) {
      triggerMaxExceeded();
    }

    if (onChange) onChange(`${formatted}:${newM.padStart(2, '0')}`);
    setTimeout(() => {
      minutesRef.current?.focus();
      minutesRef.current?.select();
    }, 0);
  };

  const handleMinutesChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    const currentH = parseInt((hoursRef.current ? hoursRef.current.value : hours) || '0', 10);

    let maxAllowedM = 59;
    if (maxMinutes !== null && maxMinutes !== undefined) {
      const remainingM = maxMinutes - (currentH * 60);
      if (remainingM <= 0) {
        maxAllowedM = 0;
      } else if (remainingM < 59) {
        maxAllowedM = remainingM;
      }
    } else if (maxHours !== null && maxHours !== undefined) {
      if (currentH >= maxHours) {
        maxAllowedM = 0;
      }
    }

    if (maxAllowedM === 0) {
      setMinutes('00');
      if (minutesRef.current) minutesRef.current.value = '00';
      if (onChange) onChange(`${String(currentH).padStart(2, '0')}:00`);
      if (raw && parseInt(raw, 10) > 0) {
        triggerMaxExceeded();
      }
      return;
    }

    if (!raw) {
      setMinutes('');
      if (onChange) onChange(`${String(currentH).padStart(2, '0')}:00`);
      return;
    }

    if (raw.length === 1) {
      let mDigit = parseInt(raw, 10);
      let exceeded = false;
      if (mDigit > maxAllowedM) {
        mDigit = maxAllowedM;
        exceeded = true;
      }
      setMinutes(String(mDigit));
      if (onChange) onChange(`${String(currentH).padStart(2, '0')}:${String(mDigit).padStart(2, '0')}`);
      if (exceeded) {
        triggerMaxExceeded();
      }
      return;
    }

    let num = parseInt(raw.slice(0, 2), 10);
    let exceeded = false;
    if (num > maxAllowedM) {
      num = maxAllowedM;
      exceeded = true;
    }
    const formattedM = String(num).padStart(2, '0');
    setMinutes(formattedM);
    if (minutesRef.current) minutesRef.current.value = formattedM;
    if (onChange) onChange(`${String(currentH).padStart(2, '0')}:${formattedM}`);
    if (exceeded) {
      triggerMaxExceeded();
    }
  };

  const handleHoursKeyDown = (e) => {
    if (e.key === 'Enter') {
      commitValue();
      return;
    }
    if (e.key === ':' || e.key === 'Tab' || e.key === 'ArrowRight' || e.key === ' ') {
      if (e.key !== 'Tab') e.preventDefault();
      const curH = (hoursRef.current ? hoursRef.current.value : hours) || '';
      if (curH && curH.length === 1) {
        const formattedH = curH.padStart(2, '0');
        setHours(formattedH);
        if (hoursRef.current) hoursRef.current.value = formattedH;
        if (onChange) onChange(`${formattedH}:${minutes || '00'}`);
      }
      minutesRef.current?.focus();
      minutesRef.current?.select();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      let exceeded = false;
      let h = parseInt((hoursRef.current ? hoursRef.current.value : hours) || '0', 10) + 1;
      if (h > effectiveMaxHours) {
        h = effectiveMaxHours;
        exceeded = true;
      }
      const formatted = String(h).padStart(2, '0');
      setHours(formatted);
      if (hoursRef.current) hoursRef.current.value = formatted;
      let m = minutes;
      if (maxMinutes !== null && maxMinutes !== undefined) {
        if (h * 60 + parseInt(m || '0', 10) > maxMinutes) {
          m = String(maxMinutes % 60).padStart(2, '0');
          setMinutes(m);
          exceeded = true;
        }
      } else if (maxHours !== null && maxHours !== undefined && h === maxHours) {
        m = '00';
        setMinutes('00');
        if (minutesRef.current) minutesRef.current.value = '00';
      }
      if (exceeded) {
        triggerMaxExceeded();
      }
      commitValue(formatted, m);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      let h = parseInt((hoursRef.current ? hoursRef.current.value : hours) || '0', 10) - 1;
      if (h < 0) h = 0;
      const formatted = String(h).padStart(2, '0');
      setHours(formatted);
      if (hoursRef.current) hoursRef.current.value = formatted;
      commitValue(formatted, minutes);
      return;
    }
  };

  const handleMinutesKeyDown = (e) => {
    if (e.key === 'Enter') {
      commitValue();
      return;
    }
    if (e.key === 'Backspace' && (minutes === '' || e.target.selectionStart === 0)) {
      hoursRef.current?.focus();
      hoursRef.current?.select();
      return;
    }
    if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) {
      e.preventDefault();
      hoursRef.current?.focus();
      hoursRef.current?.select();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const currentH = parseInt((hoursRef.current ? hoursRef.current.value : hours) || '0', 10);
      let maxAllowedM = 59;
      if (maxMinutes !== null && maxMinutes !== undefined) {
        const remainingM = maxMinutes - (currentH * 60);
        maxAllowedM = remainingM <= 0 ? 0 : Math.min(59, remainingM);
      } else if (maxHours !== null && maxHours !== undefined && currentH >= maxHours) {
        maxAllowedM = 0;
      }
      if (maxAllowedM === 0) {
        setMinutes('00');
        if (minutesRef.current) minutesRef.current.value = '00';
        return;
      }
      let m = parseInt((minutesRef.current ? minutesRef.current.value : minutes) || '0', 10) + 15;
      if (m > maxAllowedM) m = 0;
      const formattedM = String(m).padStart(2, '0');
      setMinutes(formattedM);
      if (minutesRef.current) minutesRef.current.value = formattedM;
      commitValue(hours, formattedM);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const currentH = parseInt((hoursRef.current ? hoursRef.current.value : hours) || '0', 10);
      let maxAllowedM = 59;
      if (maxMinutes !== null && maxMinutes !== undefined) {
        const remainingM = maxMinutes - (currentH * 60);
        maxAllowedM = remainingM <= 0 ? 0 : Math.min(59, remainingM);
      } else if (maxHours !== null && maxHours !== undefined && currentH >= maxHours) {
        maxAllowedM = 0;
      }
      if (maxAllowedM === 0) {
        setMinutes('00');
        if (minutesRef.current) minutesRef.current.value = '00';
        return;
      }
      let m = parseInt((minutesRef.current ? minutesRef.current.value : minutes) || '0', 10) - 15;
      if (m < 0) m = Math.floor(maxAllowedM / 15) * 15;
      const formattedM = String(m).padStart(2, '0');
      setMinutes(formattedM);
      if (minutesRef.current) minutesRef.current.value = formattedM;
      commitValue(hours, formattedM);
      return;
    }
  };

  const isSmall = size === 'small';
  const validation = validateTimeline(`${hours || '00'}:${minutes || '00'}`, maxHours, maxMinutes);
  const showError = Boolean(error || !validation.isValid);
  const defaultHelper = (maxHours === 8 && maxMinutes === null) ? 'Max 8 hours' : '';
  const displayHelperText = !validation.isValid
    ? validation.error
    : (helperText !== undefined && helperText !== '' ? helperText : (helperText === null || helperText === false ? '' : defaultHelper));

  const handleHoursBlur = (e) => {
    if (e.relatedTarget === minutesRef.current) {
      return;
    }
    setIsFocused(false);
    commitValue();
    if (onBlur) onBlur(e);
  };

  const handleMinutesBlur = (e) => {
    if (e.relatedTarget === hoursRef.current) {
      return;
    }
    setIsFocused(false);
    commitValue();
    if (onBlur) onBlur(e);
  };

  return (
    <TextField
      id={id}
      name={name}
      fullWidth={fullWidth}
      variant="outlined"
      size={size}
      label={label}
      required={required}
      error={Boolean(showError)}
      helperText={displayHelperText}
      FormHelperTextProps={{
        ...(otherProps.FormHelperTextProps || {}),
        style: {
          ...(helperTextColor ? { color: helperTextColor, opacity: 1 } : {}),
          ...((otherProps.FormHelperTextProps && otherProps.FormHelperTextProps.style) || {})
        }
      }}
      disabled={disabled}
      className={`tm-timeline-textfield ${className}`}
      onClick={() => {
        if (!disabled && hoursRef.current) {
          hoursRef.current.focus();
          hoursRef.current.select();
        }
      }}
      InputLabelProps={{
        shrink: true,
        className: 'tm-timeline-label'
      }}
      InputProps={{
        style: {
          height: isSmall ? '40px' : '56px',
          minHeight: isSmall ? '40px' : '56px',
          padding: isSmall ? '0 10px' : '0 14px',
          cursor: disabled ? 'not-allowed' : 'text'
        },
        startAdornment: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              fontSize: isSmall ? '14px' : '1rem',
              fontFamily: 'inherit',
              lineHeight: 1
            }}
          >
            <input
              ref={hoursRef}
              type="text"
              inputMode="numeric"
              placeholder="00"
              value={hours}
              maxLength={2}
              disabled={disabled}
              className="tm-time-digit-input"
              style={isSmall ? { fontSize: '14px', width: '22px' } : { width: '26px' }}
              onFocus={(e) => {
                setIsFocused(true);
                e.target.select();
                if (onFocus) onFocus();
              }}
              onBlur={handleHoursBlur}
              onChange={handleHoursChange}
              onKeyDown={handleHoursKeyDown}
            />
            <span
              className="tm-time-colon"
              style={isSmall ? { fontSize: '14px', padding: '0 2px' } : { padding: '0 3px' }}
            >
              :
            </span>
            <input
              ref={minutesRef}
              type="text"
              inputMode="numeric"
              placeholder="00"
              value={minutes}
              maxLength={2}
              disabled={disabled}
              className="tm-time-digit-input"
              style={isSmall ? { fontSize: '14px', width: '22px' } : { width: '26px' }}
              onFocus={(e) => {
                setIsFocused(true);
                e.target.select();
                if (onFocus) onFocus();
              }}
              onBlur={handleMinutesBlur}
              onChange={handleMinutesChange}
              onKeyDown={handleMinutesKeyDown}
            />
            <span
              className="tm-timeline-hr-label"
              style={isSmall ? { fontSize: '14px', fontWeight: 600, marginLeft: '5px' } : { fontSize: '16px', fontWeight: 600, marginLeft: '6px' }}
            >
              hr
            </span>
          </div>
        ),
        endAdornment: !isSmall ? (
          <InputAdornment position="end" style={{ marginLeft: 'auto' }}>
            <AccessTimeIcon
              onClick={(e) => {
                e.stopPropagation();
                if (hoursRef.current && !disabled) {
                  hoursRef.current.focus();
                  hoursRef.current.select();
                }
              }}
              style={{
                fontSize: 20,
                color: isFocused ? '#036dda' : '#94a3b8',
                cursor: disabled ? 'not-allowed' : 'pointer'
              }}
            />
          </InputAdornment>
        ) : null,
        inputProps: {
          readOnly: true,
          tabIndex: -1,
          style: {
            display: 'none'
          }
        }
      }}
      {...otherProps}
    />
  );
};

/**
 * Segmented Timeline Input for Table Cells & Detail Cards:
 * Has separate inputs for Hours [00] and Minutes [00] separated by [:] and [hr].
 * Allows clicking directly on the front [00] to change hours, or back [00] to change minutes!
 */
export const TimelineSegmentedCell = ({
  value = '',
  onSave,
  disabled = false,
  className = '',
  style = {},
  title
}) => {
  const parsed = parseTimelineHoursMinutes(value);
  const initialH = parsed ? String(parsed.h).padStart(2, '0') : '';
  const initialM = parsed ? String(parsed.m).padStart(2, '0') : '';

  const [hours, setHours] = useState(initialH);
  const [minutes, setMinutes] = useState(initialM);
  const [isFocused, setIsFocused] = useState(false);

  const hoursRef = useRef(null);
  const minutesRef = useRef(null);
  const containerRef = useRef(null);

  // Sync state if external value changes while not actively focused
  React.useEffect(() => {
    if (!isFocused) {
      const p = parseTimelineHoursMinutes(value);
      setHours(p ? String(p.h).padStart(2, '0') : '');
      setMinutes(p ? String(p.m).padStart(2, '0') : '');
    }
  }, [value, isFocused]);

  const commitSave = (hVal, mVal) => {
    const cleanH = (hVal !== undefined ? hVal : hours || '').trim();
    const cleanM = (mVal !== undefined ? mVal : minutes || '').trim();

    if (!cleanH && !cleanM) {
      if (value) {
        onSave && onSave('');
      }
      return;
    }

    let h = parseInt(cleanH || '0', 10);
    let m = parseInt(cleanM || '0', 10);
    if (isNaN(h) || h < 0) h = 0;
    if (isNaN(m) || m < 0) m = 0;
    if (m > 59) m = 59;
    if (h > 8 || (h === 8 && m > 0)) {
      h = 8;
      m = 0;
    }

    const formattedH = String(h).padStart(2, '0');
    const formattedM = String(m).padStart(2, '0');
    setHours(formattedH);
    setMinutes(formattedM);

    const finalStr = `${formattedH}:${formattedM} hr`;
    if (finalStr !== value) {
      onSave && onSave(finalStr);
    }
  };

  const handleContainerBlur = (e) => {
    // If focus moves to another element inside the same container, do not commit yet
    if (containerRef.current && containerRef.current.contains(e.relatedTarget)) {
      return;
    }
    setIsFocused(false);
    commitSave();
  };

  const handleHoursChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    if (!raw) {
      setHours('');
      return;
    }
    // Fast typing full duration (e.g. 430, 0430)
    if (raw.length >= 3) {
      const p = parseTimelineHoursMinutes(raw);
      if (p) {
        let h = p.h > 8 ? 8 : p.h;
        let m = h === 8 ? 0 : p.m;
        const formattedH = String(h).padStart(2, '0');
        const formattedM = String(m).padStart(2, '0');
        setHours(formattedH);
        setMinutes(formattedM);
        minutesRef.current?.focus();
        minutesRef.current?.select();
        return;
      }
    }
    if (raw.length === 1) {
      if (raw === '0') {
        setHours('0');
      } else {
        let num = parseInt(raw, 10);
        if (num > 8) num = 8;
        const formatted = String(num).padStart(2, '0');
        setHours(formatted);
        if (num === 8) setMinutes('00');
        // Auto advance to minutes
        minutesRef.current?.focus();
        minutesRef.current?.select();
      }
      return;
    }
    if (raw.length >= 2) {
      let num = parseInt(raw.slice(0, 2), 10);
      if (num > 8) num = 8;
      const formatted = String(num).padStart(2, '0');
      setHours(formatted);
      if (num === 8) setMinutes('00');
      // Auto advance to minutes
      minutesRef.current?.focus();
      minutesRef.current?.select();
    }
  };

  const handleMinutesChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    if (parseInt(hours || '0', 10) >= 8) {
      setMinutes('00');
      return;
    }
    if (!raw) {
      setMinutes('');
      return;
    }
    if (raw.length === 1) {
      setMinutes(raw);
      return;
    }
    let num = parseInt(raw.slice(0, 2), 10);
    if (num > 59) num = 59;
    setMinutes(String(num).padStart(2, '0'));
  };

  const handleHoursKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
      commitSave();
      return;
    }
    if (e.key === ':' || e.key === 'Tab' || e.key === 'ArrowRight') {
      if (e.key !== 'Tab') e.preventDefault();
      minutesRef.current?.focus();
      minutesRef.current?.select();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      let h = parseInt(hours || '0', 10) + 1;
      if (h > 8) h = 8;
      const formatted = String(h).padStart(2, '0');
      setHours(formatted);
      if (h === 8) setMinutes('00');
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      let h = parseInt(hours || '0', 10) - 1;
      if (h < 0) h = 0;
      setHours(String(h).padStart(2, '0'));
      return;
    }
  };

  const handleMinutesKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
      commitSave();
      return;
    }
    if (e.key === 'Backspace' && (minutes === '' || e.target.selectionStart === 0)) {
      hoursRef.current?.focus();
      hoursRef.current?.select();
      return;
    }
    if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) {
      e.preventDefault();
      hoursRef.current?.focus();
      hoursRef.current?.select();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (parseInt(hours || '0', 10) >= 8) {
        setMinutes('00');
        return;
      }
      let m = parseInt(minutes || '0', 10) + 15;
      if (m > 59) m = 0;
      setMinutes(String(m).padStart(2, '0'));
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (parseInt(hours || '0', 10) >= 8) {
        setMinutes('00');
        return;
      }
      let m = parseInt(minutes || '0', 10) - 15;
      if (m < 0) m = 45;
      setMinutes(String(m).padStart(2, '0'));
      return;
    }
  };

  const hasValue = (parseInt(hours || '0', 10) > 0 || parseInt(minutes || '0', 10) > 0);
  const tooltipText = title !== undefined ? title : (hasValue ? 'Edit timeline' : 'Add timeline');

  return (
    <div
      ref={containerRef}
      className={`tm-timeline-cell-badge ${isFocused ? 'is-focused' : ''} ${className}`}
      style={style}
      onBlur={handleContainerBlur}
      onClick={(e) => {
        if (e.target !== minutesRef.current && e.target !== hoursRef.current) {
          hoursRef.current?.focus();
        }
      }}
      title={tooltipText}
    >
      <input
        ref={hoursRef}
        type="text"
        inputMode="numeric"
        className="tm-timeline-seg-input tm-timeline-hours"
        placeholder="00"
        maxLength={3}
        disabled={disabled}
        value={hours}
        onChange={handleHoursChange}
        onFocus={(e) => {
          setIsFocused(true);
          e.target.select();
        }}
        onKeyDown={handleHoursKeyDown}
      />
      <span className="tm-timeline-sep">:</span>
      <input
        ref={minutesRef}
        type="text"
        inputMode="numeric"
        className="tm-timeline-seg-input tm-timeline-minutes"
        placeholder="00"
        maxLength={2}
        disabled={disabled}
        value={minutes}
        onChange={handleMinutesChange}
        onFocus={(e) => {
          setIsFocused(true);
          e.target.select();
        }}
        onKeyDown={handleMinutesKeyDown}
      />
      <span className="tm-timeline-unit">hr</span>
    </div>
  );
};

export default TimelineInputField;
